/**
 * Справочник тарифов (docs/billing/plan.md §2).
 *
 * Живёт в коде, а не в БД: тарифная сетка меняется редко и осознанно, деплой —
 * приемлемая цена за версионирование через git. Точечные отступления под конкретный
 * договор — `workspaces.plan_limits` (jsonb), см. {@link resolveEntitlements}.
 *
 * Один источник правды для сервера (enforcement) и веба (paywall-UI): расхождение
 * каталога между ними означало бы «кнопка разрешает, сервер отказывает».
 */

/**
 * Тарифы по возрастанию — единственный источник их порядка: апгрейд и понижение на сервере,
 * сетка карточек и таблица сравнения в вебе, списки допустимых значений в валидации.
 * Разойдись копии — UI и сервер по-разному решали бы, что «выше».
 */
export const PLAN_IDS = ['free', 'pro', 'pro_max', 'enterprise'] as const;

export type PlanId = (typeof PLAN_IDS)[number];

/** Ступень тарифа: больше — старше. */
export function planRank(plan: PlanId): number {
  return PLAN_IDS.indexOf(plan);
}

/**
 * Возможности, привязанные к тарифу.
 *
 * Первая группа гейтится в v1 (носитель существует). Вторая — «скоро»: место в
 * каталоге застолблено по прайсу, но в коде эти флаги пока никто не проверяет —
 * носители фич появятся отдельными ветками (plan.md §1).
 */
export const BILLING_FEATURES = [
  'subdomain',
  'integrations',
  'custom_fonts',
  'sheet_editor',
  'tile_formats',
  'branding_optional',
  'format_geopackage',
  'format_parquet',
  'noindex_toggle',
  'map_password',
  'analytics',
  'mcp',
] as const;

export type BillingFeature = (typeof BILLING_FEATURES)[number];

export interface PlanLimits {
  /** null — без лимита. */
  projects: number | null;
  storageBytes: number | null;
  viewsPerMonth: number | null;
  /**
   * Гости — люди не из команды, позванные в отдельные карты (docs/workspaces/plan.md §3.7).
   * Мест они не занимают, поэтому рамку им задаёт число, а не цена.
   */
  guests: number | null;
}

/** Ключи лимитов — один список для чтения override'ов и их применения. */
const PLAN_LIMIT_KEYS = ['projects', 'storageBytes', 'viewsPerMonth', 'guests'] as const satisfies readonly (keyof PlanLimits)[];

/** Лимиты, пришедшие по сети, — проверка формы вместо `as`: каждый ключ — число ≥ 0 или null. */
export function isPlanLimits(value: unknown): value is PlanLimits {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const raw = value as Record<string, unknown>;
  return PLAN_LIMIT_KEYS.every((key) => {
    const limit = raw[key];
    return limit === null || (typeof limit === 'number' && Number.isFinite(limit) && limit >= 0);
  });
}

/**
 * Ступень цены места: `upTo` — включительно, null — «и дальше». Цены — за место в месяц:
 * `monthlyRub` при помесячной оплате, `annualMonthlyRub` — при оплате на год вперёд
 * (за год списывается двенадцать таких сумм). Прайс — calc.anycraft.app, регион «Россия».
 */
export interface PerSeatTier {
  upTo: number | null;
  monthlyRub: number;
  annualMonthlyRub: number;
}

/**
 * Как часто списывается подписка (docs/billing/plan.md §4b): раз в месяц или раз в год со
 * скидкой. Годовой срок оплачивает места вперёд: уменьшить их до конца срока нельзя.
 */
export const BILLING_INTERVALS = ['month', 'year'] as const;

export type BillingInterval = (typeof BILLING_INTERVALS)[number];

/** Сырое значение из БД → интервал. Неизвестное читается как месяц — так жили все строки до §4b. */
export function resolveBillingInterval(raw: string | null | undefined): BillingInterval {
  return raw === 'year' ? 'year' : 'month';
}

/** Сколько месяцев оплачивает одно списание. */
export function intervalMonths(interval: BillingInterval): number {
  return interval === 'year' ? 12 : 1;
}

/**
 * Потолок мест одной покупки самообслуживанием: защита от опечатки «1000» вместо «10» на
 * годовой цене. Больше — по договору «Компании».
 */
export const MAX_SELF_SERVE_SEATS = 10_000;

export type PlanPricing =
  | { kind: 'free' }
  | { kind: 'flat'; monthlyRub: number }
  | { kind: 'per_seat'; tiers: readonly PerSeatTier[] }
  | { kind: 'custom' };

export interface PlanDef {
  id: PlanId;
  /** Название для UI («Старт», «PRO», …). */
  title: string;
  pricing: PlanPricing;
  limits: PlanLimits;
  features: readonly BillingFeature[];
}

const MB = 1024 * 1024;
const GB = 1024 * MB;

const PRO_FEATURES: readonly BillingFeature[] = [
  'custom_fonts',
  'sheet_editor',
  'tile_formats',
  'branding_optional',
  'noindex_toggle',
  'format_geopackage',
  'format_parquet',
];

const PRO_MAX_FEATURES: readonly BillingFeature[] = [
  ...PRO_FEATURES,
  'subdomain',
  'integrations',
  'map_password',
  'analytics',
  // ИИ-агенты через MCP (`/api/mcp`, docs/mcp/plan.md): только старшие тарифы — решение
  // владельца 2026-10-10. Гейт — по воркспейсу карты, как у всех фич: токен личный, но
  // агент работает только с картами команд, чей тариф это включает.
  'mcp',
];

export const PLANS: Record<PlanId, PlanDef> = {
  free: {
    id: 'free',
    title: 'Старт',
    pricing: { kind: 'free' },
    limits: { projects: 3, storageBytes: 250 * MB, viewsPerMonth: 10_000, guests: 5 },
    features: [],
  },
  pro: {
    id: 'pro',
    title: 'PRO',
    // За место, как и PRO MAX (решение владельца 2026-08-16): флэт позволял одному
    // оплаченному воркспейсу обслуживать сколько угодно редакторов. Ступень одна —
    // объёмная скидка остаётся отличием старшего тарифа, здесь цена места постоянна.
    pricing: { kind: 'per_seat', tiers: [{ upTo: null, monthlyRub: 1490, annualMonthlyRub: 1190 }] },
    limits: { projects: null, storageBytes: 10 * GB, viewsPerMonth: 50_000, guests: 25 },
    features: PRO_FEATURES,
  },
  pro_max: {
    id: 'pro_max',
    title: 'PRO MAX',
    pricing: {
      kind: 'per_seat',
      tiers: [
        { upTo: 5, monthlyRub: 2990, annualMonthlyRub: 2490 },
        { upTo: 20, monthlyRub: 2790, annualMonthlyRub: 2290 },
        // Дальше — без потолка: «редакторы без ограничений» (решение владельца 2026-10-10,
        // прайс calc.anycraft.app). Раньше после 50 мест вела только «Компания».
        { upTo: null, monthlyRub: 2690, annualMonthlyRub: 2190 },
      ],
    },
    limits: { projects: null, storageBytes: 50 * GB, viewsPerMonth: 250_000, guests: 100 },
    features: PRO_MAX_FEATURES,
  },
  enterprise: {
    id: 'enterprise',
    title: 'Компания',
    pricing: { kind: 'custom' },
    // База — без лимитов: настоящие рамки конкретного договора задаются через
    // `plan_limits` руками из админки.
    //
    // Гостей это не касается: `plan_limits` только расширяют (`resolveEntitlements`), и
    // сузить «без лимита» нечем — у «Компании» гостей сколько угодно, как и всего прочего.
    limits: { projects: null, storageBytes: null, viewsPerMonth: null, guests: null },
    features: PRO_MAX_FEATURES,
  },
};

/**
 * На «Старте» редактирует один человек — владелец команды (прайс: «1 редактор, зрители и
 * комментаторы бесплатно»). Позвать второго редактора — повод для PRO; зрителей и
 * комментаторов можно сколько угодно. Если команда уже была больше (тариф кончился),
 * остальные редакторы на «Старте» только смотрят и комментируют — данные и роли целы,
 * с платным тарифом права возвращаются сами.
 */
export const FREE_PLAN_EDITORS = 1;

/**
 * Русские названия тарифов — те же, что на странице тарифов («Про», «Про Макс»). Один источник
 * для веба (кнопки, тексты, тосты) и сервера (наименования позиций в фискальном чеке): иначе
 * человек видел бы в интерфейсе одно, а в чеке другое. `PLANS[...].title` остаётся латиницей
 * («PRO», «PRO MAX») — оно идёт в описание платежа CloudPayments, не в чек.
 */
export const PLAN_DISPLAY_TITLE: Record<PlanId, string> = {
  free: 'Старт',
  pro: 'Про',
  pro_max: 'Про Макс',
  enterprise: 'Компания',
};

/** Тариф считается по местам? (free и «Компания» — нет.) */
export function isPerSeatPlan(planId: PlanId): boolean {
  return PLANS[planId].pricing.kind === 'per_seat';
}

/**
 * Сырой `workspaces.plan` → известный план. Неизвестное значение читается как free:
 * опечатка в БД не должна молча раздавать возможности. Legacy-план `business`
 * (заглушка времён workspaces) трактуем как pro_max (plan.md §2).
 */
export function resolvePlanId(raw: string | null | undefined): PlanId {
  const plan = (raw ?? '').trim();
  if (plan === 'business') {
    return 'pro_max';
  }
  if (plan === 'free' || plan === 'pro' || plan === 'pro_max' || plan === 'enterprise') {
    return plan;
  }
  return 'free';
}

/** Ступень цены при данном числе мест — одна на всю команду. */
function seatTier(tiers: readonly PerSeatTier[], seats: number): PerSeatTier {
  for (const tier of tiers) {
    if (tier.upTo === null || seats <= tier.upTo) {
      return tier;
    }
  }
  // Недостижимо, пока последний tier открыт (upTo: null); страховка от правки каталога.
  return tiers[tiers.length - 1]!;
}

/** Цена места ступени за месяц: помесячно или месячная доля годовой цены. */
export function tierPriceRub(tier: PerSeatTier, interval: BillingInterval): number {
  return interval === 'year' ? tier.annualMonthlyRub : tier.monthlyRub;
}

/**
 * Цена одного места за месяц при данном числе платных мест. У PRO ступень одна,
 * у PRO MAX место дешевеет с ростом команды. При годовой оплате — месячная доля годовой
 * цены (за год — двенадцать таких). Непосеточный тариф — `null`.
 */
export function seatPriceRub(
  planId: PlanId,
  seats: number,
  interval: BillingInterval = 'month',
): number | null {
  const pricing = PLANS[planId].pricing;
  if (pricing.kind !== 'per_seat') {
    return null;
  }
  return tierPriceRub(seatTier(pricing.tiers, seats), interval);
}

/**
 * Месячная цена плана в рублях. `null` — план не покупается самообслуживанием
 * (free, enterprise). Посеточные — без потолка мест: последняя ступень открыта.
 */
export function planMonthlyPriceRub(planId: PlanId, billableSeats: number): number | null {
  return planPeriodPriceRub(planId, billableSeats, 'month');
}

/**
 * Сумма одного списания подписки: за месяц или сразу за год (двенадцать месячных долей
 * годовой цены). `null` — план не покупается самообслуживанием (free, enterprise).
 */
export function planPeriodPriceRub(
  planId: PlanId,
  billableSeats: number,
  interval: BillingInterval,
): number | null {
  const pricing = PLANS[planId].pricing;
  switch (pricing.kind) {
    case 'flat':
      return pricing.monthlyRub * intervalMonths(interval);
    case 'per_seat': {
      // Воркспейс без платных участников невозможен (owner всегда billable),
      // но защищаемся от нуля, чтобы не выставить счёт на 0 ₽.
      const seats = Math.max(1, billableSeats);
      const price = seatPriceRub(planId, seats, interval);
      return price === null ? null : seats * price * intervalMonths(interval);
    }
    default:
      return null;
  }
}

/**
 * Сколько мест оплатило списание `amountRub`: наибольшее число мест, чья цена за период не
 * больше суммы. Так годовое продление узнаёт оплаченные места по самим деньгам, а не по
 * составу в момент уведомления: CloudPayments мог списать прежнюю сумму подписки, и места
 * сверх неё доплатятся отдельно. Цена по ступеням растёт с числом мест монотонно — на
 * границе ступени место дешевеет, но общая сумма всё равно больше. `null` — тариф не
 * посеточный или сумма меньше одного места.
 */
export function seatsCoveredByRub(
  planId: PlanId,
  amountRub: number,
  interval: BillingInterval,
): number | null {
  const pricing = PLANS[planId].pricing;
  if (pricing.kind !== 'per_seat' || !(amountRub > 0)) {
    return null;
  }
  const months = intervalMonths(interval);
  let best: number | null = null;
  let from = 1;
  for (const tier of pricing.tiers) {
    const price = tierPriceRub(tier, interval) * months;
    // Полкопейки допуска: суммы в журнале — numeric(12,2), цены каталога — целые рубли.
    const fits = Math.floor((amountRub + 0.005) / price);
    const seats = tier.upTo === null ? fits : Math.min(tier.upTo, fits);
    if (seats >= from) {
      best = Math.max(best ?? 0, seats);
    }
    if (tier.upTo === null) {
      break;
    }
    from = tier.upTo + 1;
  }
  return best;
}

/** Насколько месячная доля годовой цены ниже помесячной, в целых процентах; `null` — не ниже. */
function discountPercent(monthlyRub: number, annualMonthlyRub: number): number | null {
  return monthlyRub > 0 && annualMonthlyRub < monthlyRub
    ? Math.round(((monthlyRub - annualMonthlyRub) / monthlyRub) * 100)
    : null;
}

/**
 * Скидка годовой оплаты к помесячной на ступени `seats`, в процентах (округление до целого).
 * `null` — у тарифа нет цены места или скидки.
 */
export function annualDiscountPercent(planId: PlanId, seats = 1): number | null {
  const monthly = seatPriceRub(planId, Math.max(1, seats), 'month');
  const annual = seatPriceRub(planId, Math.max(1, seats), 'year');
  return monthly === null || annual === null ? null : discountPercent(monthly, annual);
}

/**
 * Наименьшая годовая скидка среди посеточных тарифов — для переключателя «На год −N %»: её
 * получит любой, кто платит за год. Наибольшая (20 % у Про) на переключателе обещала бы больше,
 * чем выйдет у Про Макс (17–19 % по ступеням).
 */
export function minAnnualDiscountPercent(): number | null {
  let least: number | null = null;
  for (const planId of PLAN_IDS) {
    const pricing = PLANS[planId].pricing;
    if (pricing.kind !== 'per_seat') {
      continue;
    }
    for (const tier of pricing.tiers) {
      const percent = discountPercent(tier.monthlyRub, tier.annualMonthlyRub);
      if (percent !== null) {
        least = Math.min(least ?? percent, percent);
      }
    }
  }
  return least;
}

/* ------------------------------------------------------------------ */
/*  Пробный период                                                     */
/* ------------------------------------------------------------------ */

/**
 * Бесплатные дни при первом переходе с бесплатного «Старта» на платный тариф
 * (решение владельца 2026-08-16): карта указывается сразу, первое списание — после.
 */
export const TRIAL_DAYS = 14;

/**
 * Сумма проверочной блокировки карты при старте пробного периода.
 *
 * Платёжного токена без транзакции CloudPayments не выдаёт, а подписка
 * (`subscriptions/create`) без токена невозможна — поэтому карта проверяется
 * двухстадийным платежом на минимальную сумму, которую мы тут же отменяем
 * (деньги разблокируются мгновенно, чек не пробивается — платежа не было).
 */
export const TRIAL_CARD_HOLD_RUB = 1;

/** Конец пробного периода от момента подключения. */
export function trialEndsAt(from: Date | number = Date.now()): Date {
  const start = typeof from === 'number' ? from : from.getTime();
  return new Date(start + TRIAL_DAYS * 86_400_000);
}

/* ------------------------------------------------------------------ */
/*  Истечение оплаченного периода                                      */
/* ------------------------------------------------------------------ */

/** Сбой продления: сколько дней план живёт после конца оплаченного периода (plan.md §4). */
export const BILLING_GRACE_DAYS = 3;

/**
 * Договор по счёту: сколько дней тариф живёт после `paid_until` (plan.md §4). Банковский
 * перевод идёт 1–3 рабочих дня, и клиент, оплативший новый счёт в последний день срока,
 * иначе наутро оказывался бы на «Старте» — с редакторами, которые только смотрят.
 * Неделя перекрывает выходные и праздники.
 */
export const INVOICE_GRACE_DAYS = 7;

/**
 * За сколько дней до конца срока по счёту напоминаем о новом счёте: владельцу — плашкой,
 * оператору — меткой и фильтром «требуют внимания». Банковский перевод идёт несколько дней.
 */
export const INVOICE_RENEWAL_NOTICE_DAYS = 14;

const DAY_MS = 86_400_000;

/** Состояние оплаты в объёме, нужном для ответа «период ещё действует?». */
export interface BillingPeriodState {
  source?: string | null;
  status?: string | null;
  currentPeriodEnd?: Date | string | null;
  paidUntil?: Date | string | null;
}

function asTime(value: Date | string | null | undefined): number | null {
  if (!value) {
    return null;
  }
  const time = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
}

/**
 * Оплаченный период закончился?
 *
 * Смотрим на ДАТУ, а не на статус подписки: строка может остаться `active` навсегда —
 * например, если подписка в CloudPayments так и не создалась и продлевать нечего.
 * Именно дата — единственное, что нельзя подделать со стороны клиента (plan.md §4).
 *
 * `admin_test` и строки без данных о периоде не истекают: первое выключается руками,
 * второе — план, выданный мимо биллинга, и понижать его нам нечем.
 */
export function isBillingPeriodExpired(
  billing: BillingPeriodState | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!billing) {
    return false;
  }
  // Grace нужен ровно затем, чтобы пережить ретраи неудавшегося списания. У отменённой
  // подписки ретраев не будет: продлевать нечем, и лишние три дня — просто подарок
  // сверх оплаченного периода. На пробном периоде это заметнее всего: отменивший до
  // первого списания получал бы 17 дней там, где интерфейс обещает 14.
  const graceMs = billing.status === 'cancelled' ? 0 : BILLING_GRACE_DAYS * DAY_MS;
  if (billing.source === 'cloudpayments') {
    const end = asTime(billing.currentPeriodEnd);
    return end !== null && end + graceMs < now;
  }
  if (billing.source === 'manual') {
    const until = asTime(billing.paidUntil);
    return until !== null && until + INVOICE_GRACE_DAYS * DAY_MS < now;
  }
  return false;
}

/**
 * Последний момент, когда тариф по счёту ещё действует: `paid_until` + {@link INVOICE_GRACE_DAYS}.
 * null — бессрочный договор.
 */
export function invoiceGraceEndsAt(paidUntil: Date | string | null | undefined): Date | null {
  const until = asTime(paidUntil);
  return until === null ? null : new Date(until + INVOICE_GRACE_DAYS * DAY_MS);
}

/**
 * Последний момент, когда живёт подписка с неудавшимся списанием (`past_due`): конец
 * периода + {@link BILLING_GRACE_DAYS} — пока CloudPayments ретраит списание.
 */
export function subscriptionGraceEndsAt(
  currentPeriodEnd: Date | string | null | undefined,
): Date | null {
  const end = asTime(currentPeriodEnd);
  return end === null ? null : new Date(end + BILLING_GRACE_DAYS * DAY_MS);
}

/**
 * Срок по счёту ещё идёт, но до конца — не больше {@link INVOICE_RENEWAL_NOTICE_DAYS}:
 * пора выставлять новый счёт. Закончившийся срок сюда не попадает — это уже запас
 * ({@link isInvoiceGracePeriod}).
 */
export function isInvoiceRenewalDue(
  billing: BillingPeriodState | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!billing || billing.source !== 'manual' || billing.status === 'expired') {
    return false;
  }
  const until = asTime(billing.paidUntil);
  return until !== null && until >= now && until - now <= INVOICE_RENEWAL_NOTICE_DAYS * DAY_MS;
}

/**
 * Срок по счёту закончился, но тариф ещё живёт запасом ({@link INVOICE_GRACE_DAYS}): ждём
 * оплату нового счёта. Срок при этом НЕ идёт — места сверх договора не копятся, подъём
 * тарифа доплатой не продаётся, а картой можно купить обычную подписку.
 */
export function isInvoiceGracePeriod(
  billing: BillingPeriodState | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!billing || billing.source !== 'manual' || billing.status === 'expired') {
    return false;
  }
  const until = asTime(billing.paidUntil);
  return until !== null && until < now && !isBillingPeriodExpired(billing, now);
}

/**
 * Подписка ещё способна списать деньги? `trialing` считается живой наравне с `active` и
 * `past_due`: подписка в CP уже заведена и спишет в конце пробного периода. Ручная
 * выдача плана поверх такой строки переводит `source` в `manual`/`admin_test`, после
 * чего Pay-вебхук её игнорирует, — деньги ушли бы, а тариф из них не восстановился.
 *
 * В общем пакете: тем же правилом диалог админки решает, блокировать ли ручную выдачу —
 * иначе он пускал бы её на триале и `past_due`, а сервер отвечал бы 409.
 */
export function isLiveCpSubscription(
  billing: { source?: string | null; status?: string | null } | null | undefined,
): boolean {
  return (
    billing?.source === 'cloudpayments' &&
    (billing.status === 'active' || billing.status === 'trialing' || billing.status === 'past_due')
  );
}

/**
 * План с учётом истечения периода: просроченная оплата читается как `free` ещё до того,
 * как ночной джоб перепишет `workspaces.plan` (plan.md §4 — «ловим лениво при чтении»).
 */
export function effectivePlanId(
  plan: string | null | undefined,
  billing: BillingPeriodState | null | undefined,
  now?: number,
): PlanId {
  const planId = resolvePlanId(plan);
  if (planId === 'free') {
    return planId;
  }
  return isBillingPeriodExpired(billing, now) ? 'free' : planId;
}

/** Возможности и лимиты воркспейса после применения `plan_limits`. Сериализуемо. */
export interface WorkspaceEntitlements {
  planId: PlanId;
  features: BillingFeature[];
  limits: PlanLimits;
}

/**
 * Возможности воркспейса, когда тарифы «спят» (`PAYMENTS_ENABLED=false` на сервере): все
 * платные функции открыты, лимитов нет — стенд ведёт себя как до появления биллинга.
 * Считает сервер и отдаёт клиенту в обычном поле `entitlements`, поэтому замки и paywall
 * в вебе гаснут сами, без отдельной ветки «оплаты выключены» в каждом гейте.
 *
 * `planId` — старший тариф: проверки вида «на «Старте» один редактор» тоже не срабатывают.
 */
export function unrestrictedEntitlements(): WorkspaceEntitlements {
  return {
    planId: 'enterprise',
    features: [...BILLING_FEATURES],
    limits: { projects: null, storageBytes: null, viewsPerMonth: null, guests: null },
  };
}

/** Выводится из каталога: новая фича в `BILLING_FEATURES` сразу проходит валидацию override'ов. */
const KNOWN_FEATURES: ReadonlySet<string> = new Set<string>(BILLING_FEATURES);

/** `plan_limits` приходит из jsonb — форму проверяем, а не утверждаем через `as`. */
function readOverrides(planLimits: unknown): {
  features: BillingFeature[];
  limits: Partial<PlanLimits>;
} {
  if (!planLimits || typeof planLimits !== 'object' || Array.isArray(planLimits)) {
    return { features: [], limits: {} };
  }
  const raw = planLimits as Record<string, unknown>;

  const features: BillingFeature[] = [];
  if (Array.isArray(raw.features)) {
    for (const item of raw.features) {
      if (typeof item === 'string' && KNOWN_FEATURES.has(item.trim())) {
        features.push(item.trim() as BillingFeature);
      }
    }
  }

  const limits: Partial<PlanLimits> = {};
  const rawLimits =
    raw.limits && typeof raw.limits === 'object' && !Array.isArray(raw.limits)
      ? (raw.limits as Record<string, unknown>)
      : {};
  for (const key of PLAN_LIMIT_KEYS) {
    const value = rawLimits[key];
    if (value === null) {
      limits[key] = null;
    } else if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
      limits[key] = value;
    }
  }
  return { features, limits };
}

/**
 * План + `plan_limits` → итоговые возможности и лимиты.
 *
 * Переопределения только расширяют: фичи — union, лимиты — берём больший (или null
 * как «без лимита»). Отзыв возможностей = смена плана, не override (plan.md §2,
 * правило из workspaces: «только выдача, не отзыв»).
 */
export function resolveEntitlements(
  plan: string | null | undefined,
  planLimits: unknown,
  /** Состояние оплаты: просроченный период понижает план до free ещё до ночного джоба. */
  billing?: BillingPeriodState | null,
): WorkspaceEntitlements {
  const planId = effectivePlanId(plan, billing);
  const def = PLANS[planId];
  const overrides = readOverrides(planLimits);

  const features = new Set<BillingFeature>(def.features);
  for (const feature of overrides.features) {
    features.add(feature);
  }

  const limits: PlanLimits = { ...def.limits };
  for (const key of PLAN_LIMIT_KEYS) {
    if (!(key in overrides.limits)) {
      continue;
    }
    const override = overrides.limits[key];
    const base = limits[key];
    if (override === null) {
      limits[key] = null;
    } else if (override !== undefined && base !== null && override > base) {
      limits[key] = override;
    }
  }

  return { planId, features: [...features], limits };
}

/**
 * Минимальный платный план, дающий возможность, — для paywall-сообщений
 * («нужен PRO» / «нужен PRO MAX»).
 */
export function requiredPlanFor(feature: BillingFeature): PlanId {
  return PLANS.pro.features.includes(feature) ? 'pro' : 'pro_max';
}

/**
 * Повод показать paywall: фича тарифа или упор в числовой лимит.
 * Ключ общий для серверного 403 `{code: 'plan_limit', feature}` и текстов модалки —
 * сервер и веб обязаны говорить на одном словаре.
 * `views` здесь нет: лимит просмотров мягкий и ничего не блокирует (plan.md §6).
 * `team` — второй редактор на «Старте» ({@link FREE_PLAN_EDITORS}).
 * `guests` — упор в {@link PlanLimits.guests}.
 */
export type PaywallReason = BillingFeature | 'projects' | 'storage' | 'team' | 'guests';

/** Следующий тариф вверх; null — выше только «Компания» (переговоры). */
export function nextPlanUp(plan: PlanId): PlanId | null {
  if (plan === 'free') {
    return 'pro';
  }
  if (plan === 'pro') {
    return 'pro_max';
  }
  return null;
}

/** Какой план предлагать в paywall'е: для лимитов — следующий вверх от текущего. */
export function requiredPlanForReason(reason: PaywallReason, currentPlan: PlanId): PlanId {
  if (reason === 'team') {
    return 'pro';
  }
  if (reason === 'projects' || reason === 'storage') {
    return nextPlanUp(currentPlan) ?? 'pro_max';
  }
  // Больше сотни гостей у PRO MAX — уже договор «Компании», а не следующий тариф сетки.
  if (reason === 'guests') {
    return nextPlanUp(currentPlan) ?? 'enterprise';
  }
  return requiredPlanFor(reason);
}
